/** @format */

import { produce } from "immer";
import { cardsApi } from "../../app/services/resource/cardsApi";
import {
  userCardsApi,
  UserResourceCardBody,
} from "../../app/services/userResource/userCardsApi";
import { userLessonApi } from "../../app/services/userResource/userLessonApi";
import { userResourceApi } from "../../app/services/userResource/userResourcesApi";
import { store } from "../../app/store";
import { getResourceCode } from "../../utils/GetResourceCode";
import { staticCardTypes } from "../contentEditor/utils";
import { Locale, LocaleText } from "../i18n/init";
import { moveItemDown, moveItemUp } from "../resources/ResourceFunctions";
import {
  Card,
  CardReference,
  CardType,
  Lesson,
  ResourceObject,
  Sublesson,
  UserResource,
} from "../resources/types";

export type UuidString = string & { _brand: "UuidString" };

export const isValidUuid = (uuid: string) => {
  const regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regex.test(uuid.toLowerCase());
};

export const getRandomUUID = () => crypto.randomUUID() as UuidString;

export const checkSublesson = (
  sublesson: Omit<Sublesson, "uuid"> & { uuid?: string },
): Omit<Sublesson, "title"> & { title: Partial<LocaleText> } => {
  let _sublesson = { ...sublesson };

  if (_sublesson.originalUUID !== undefined) {
    if (!_sublesson.uuid) {
      _sublesson.uuid = getRandomUUID();
    }
  } else {
    if (!!_sublesson.uuid) {
      _sublesson.originalUUID = _sublesson.uuid;
    }
    _sublesson.uuid = getRandomUUID();
  }

  return _sublesson as Omit<Sublesson, "title"> & {
    title: Partial<LocaleText>;
  };
};

/**
 * @returns saved card's UUID
 */
export const addCard = async (
  type: CardType,
  locale: Locale,
  resource: UserResource,
  lessonIndex: number,
  sublessonIndex: number,
  newIndex: number,
) => {
  const sublesson = checkSublesson(
    resource.Object.lessons[lessonIndex].subLessons[sublessonIndex],
  );

  const _staticCardTypes = staticCardTypes.filter(
    (type) => !["gallery", "carousel"].includes(type.id),
  );

  // Fetch example.json from activity or read card

  const data = await import(
    `../activityCards/cards/${
      _staticCardTypes.find((_type) => type === _type.id)
        ? "Static"
        : type.charAt(0).toUpperCase() + type.slice(1)
    }Card/template.json`
  )
    .then((json) => {
      return json.data;
    })
    .catch(() => {
      throw Error("Unable to retrieve correct data");
    });

  const card: Omit<Card, "UUID" | "PK" | "SK" | "created" | "modified"> & {
    UUID?: string;
    PK?: string;
    SK?: string;
    created?: string;
    modified?: string;
  } = {
    Object: {
      type: type,
      data: data,
    },
    Parent: sublesson.uuid,
  };

  const _card = checkCard(
    card,
    getResourceCode(resource),
    locale,
    sublesson.uuid,
    resource.Copy,
  );
  await saveCard(
    _card,
    locale,
    resource,
    lessonIndex,
    sublessonIndex,
    newIndex,
  );

  return _card.UUID;
};

/**
 * @returns saved card's UUID
 */
export const saveCard = async (
  card: Card,
  locale: Locale,
  resource: UserResource,
  lessonIndex: number,
  sublessonIndex: number,
  newIndex?: number,
) => {
  const dispatch = store.dispatch;
  const sublesson = checkSublesson(
    resource.Object.lessons[lessonIndex].subLessons[sublessonIndex],
  );
  const _card = checkCard(
    card,
    getResourceCode(resource),
    locale,
    sublesson.uuid,
    resource.Copy,
  );

  const body: UserResourceCardBody = {
    Code: getResourceCode(resource),
    Locale: locale,
    Object: _card.Object,
    Parent: sublesson.uuid,
    PK: _card.PK,
    SK: _card.SK,
    UUID: _card.UUID,
    originalUUID: _card.originalUUID,
  };

  // save card as userResourcecard
  const saveCardRes = await dispatch(
    userCardsApi.endpoints.saveUserResourceCard.initiate({
      body,
      code: getResourceCode(resource),
      locale,
      copy: resource.Copy,
    }),
  ).unwrap();

  // TODO: does this handle save card errors?
  if (saveCardRes) {
    const newCard = body;
    const selectedCardIndex = (
      resource.Object?.lessons?.[lessonIndex]?.subLessons?.[sublessonIndex]
        ?.cards ?? []
    ).findIndex((item) => item.uuid === newCard.originalUUID);

    const newCardReference: CardReference = {
      type: newCard.Object.type,
      uuid: newCard.UUID,
      originalUUID: newCard.originalUUID,
    };

    let newCards: CardReference[] = [
      ...(resource.Object?.lessons?.[lessonIndex]?.subLessons?.[sublessonIndex]
        ?.cards ?? []),
    ];

    if (selectedCardIndex >= 0) {
      newCards.splice(selectedCardIndex, 1, newCardReference);
    } else {
      if (newIndex !== undefined) {
        newCards.splice(newIndex, 0, newCardReference);
      }
    }

    let newResource = produce(resource, (draft) => {
      draft.Object.lessons[lessonIndex].subLessons[sublessonIndex] = {
        ...sublesson,
        cards: newCards,
      };
    });

    try {
      await dispatch(
        userLessonApi.endpoints.saveUserLesson.initiate({
          diff: { Object: { lessons: newResource.Object.lessons } },
          primaryKey: { PK: resource.PK, SK: resource.SK },
          userEmailHash: resource.UserEmailHash,
          locale: resource.Locale,
          copy: resource.Copy,
        }),
      ).unwrap();
    } catch {
      console.log("Failed");
    }
  }

  return _card.UUID;
};

export const generateUserCardSK = (
  subLessonUuid: UuidString,
  cardUuid: UuidString,
): string => `CARD#PARENT:${subLessonUuid}#UUID:${cardUuid}`;
export const generateUserCardPK = (
  code: string,
  locale: Locale,
  copy: number,
): string =>
  `USER_BL#${code.toUpperCase()}#LOCALE:${locale.toUpperCase()}#COPY:${copy}`;

/** Check if card is a reference to a resource or an original card of a UserResource */
export const checkCard = (
  card: Omit<Card, "UUID" | "PK" | "SK" | "created" | "modified"> & {
    UUID?: string;
    PK?: string;
    SK?: string;
    created?: string;
    modified?: string;
  },
  code: string,
  locale: Locale,
  parent: string,
  copy: number,
): Card => {
  let _card = { ...card };
  if (card.originalUUID !== undefined) {
    return _card as Card;
  } else {
    if (_card.UUID !== undefined) {
      _card.originalUUID = _card.UUID;
    } else {
      _card.originalUUID = "";
    }
    _card.UUID = getRandomUUID();
    _card.PK = generateUserCardPK(code, locale, copy);
    _card.SK = generateUserCardSK(
      parent as UuidString,
      _card.UUID as UuidString,
    );
    return _card as Card;
  }
};

type props = {
  resource: ResourceObject;
  code: string;
  lessonIndex: number;
  subLessonUuid: string;
  locale: Locale;
  copy: number;
};

export const getUserResourceSubLessonCards = async ({
  resource,
  code,
  lessonIndex,
  subLessonUuid,
  locale,
  copy,
}: props): Promise<Card[] | undefined> => {
  const dispatch = store.dispatch;

  const sublesson = resource.lessons[lessonIndex].subLessons.find(
    (item) => item.uuid === subLessonUuid,
  );

  if (sublesson) {
    // Is the sublesson an original
    if (sublesson.originalUUID !== undefined) {
      const cardUuids = sublesson.cards
        .filter((item) => !!item.originalUUID)
        ?.map((item) => item.uuid);
      let resourceCards: Card[] = [];
      if (cardUuids.length > 0) {
        const { data: _resourceCards } = await dispatch(
          cardsApi.endpoints.getResourceSubLessonCards.initiate({
            resourceId: code,
            subLessonUuid: sublesson.originalUUID,
            cardUuids,
          }),
        );
        resourceCards = _resourceCards ?? [];
      }
      const { data: userResourceCards } = await dispatch(
        userCardsApi.endpoints.getUserResourceSubLessonCards.initiate({
          resourceId: code,
          locale,
          subLessonUuid,
          copy,
        }),
      );

      const cards = (resourceCards ?? []).concat(userResourceCards ?? []);
      const cardOrder = [...(sublesson.cards ?? [])].map((item) => item.uuid);

      return [...cards].sort(
        (a, b) => cardOrder.indexOf(a.UUID) - cardOrder.indexOf(b.UUID),
      );
    } else {
      const cards = await dispatch(
        cardsApi.endpoints.getResourceSubLessonCards.initiate({
          resourceId: code,
          subLessonUuid,
        }),
      ).unwrap();
      const cardOrder = [...(sublesson.cards ?? [])].map((item) => item.uuid);

      return [...cards].sort(
        (a, b) => cardOrder.indexOf(a.UUID) - cardOrder.indexOf(b.UUID),
      );
    }
  }

  return undefined;
};

export const moveCard = async (
  resourceId: string,
  lessonIndex: number,
  sublessonIndex: number,
  itemUUID: string,
  action: "move-up" | "move-down",
  locale: Locale,
  copy: number,
  userId: string,
) => {
  const dispatch = store.dispatch;

  const { data: resource } = await dispatch(
    userResourceApi.endpoints.getUserResource.initiate({
      code: resourceId,
      copy,
      locale,
      email: userId,
    }),
  );

  if (resource) {
    const selectedCardIndex = resource.Object.lessons[lessonIndex].subLessons[
      sublessonIndex
    ].cards.findIndex((item) => item.uuid === itemUUID);
    let currentCards: CardReference[] = [
      ...resource.Object.lessons[lessonIndex].subLessons[sublessonIndex].cards,
    ];

    let newCards: CardReference[] | undefined;

    if (action === "move-up") {
      newCards = moveItemUp(currentCards, selectedCardIndex) as
        | CardReference[]
        | undefined;
    }

    if (action === "move-down") {
      newCards = moveItemDown(currentCards, selectedCardIndex) as
        | CardReference[]
        | undefined;
    }

    if (newCards !== undefined) {
      let newResource = produce(resource, (draft) => {
        let sublesson =
          draft.Object.lessons[lessonIndex].subLessons[sublessonIndex];
        draft.Object.lessons[lessonIndex].subLessons[sublessonIndex] =
          checkSublesson({ ...sublesson, cards: newCards ?? currentCards });
      });

      try {
        await dispatch(
          userResourceApi.endpoints.saveUserResource.initiate({
            diff: { Object: { lessons: newResource.Object.lessons } },
            primaryKey: { PK: resource.PK, SK: resource.SK },
            userEmailHash: resource.UserEmailHash,
            locale: resource.Locale,
            copy: resource.Copy,
          }),
        );
      } catch {
        console.log("Move Card Failed");
      }
    }
  }
};

export const moveLesson = async (
  resourceId: string,
  action: "move-up" | "move-down",
  itemUUID: string,
  copy: number,
  locale: Locale,
  userId: string,
) => {
  const dispatch = store.dispatch;

  const { data: resource } = await dispatch(
    userResourceApi.endpoints.getUserResource.initiate({
      code: resourceId,
      copy,
      locale,
      email: userId,
    }),
  );

  if (resource) {
    const selectedLessonIndex = resource.Object.lessons.findIndex(
      (item) => item.uuid === itemUUID,
    );
    let currentLessons: Lesson[] = [...resource.Object.lessons];

    let newLessons: Lesson[] | undefined;

    if (action === "move-up") {
      newLessons = moveItemUp(currentLessons, selectedLessonIndex) as
        | Lesson[]
        | undefined;
    }

    if (action === "move-down") {
      newLessons = moveItemDown(currentLessons, selectedLessonIndex) as
        | Lesson[]
        | undefined;
    }

    if (newLessons !== undefined) {
      try {
        await dispatch(
          userResourceApi.endpoints.saveUserResource.initiate({
            diff: { Object: { lessons: newLessons ?? currentLessons } },
            primaryKey: { PK: resource.PK, SK: resource.SK },
            userEmailHash: resource.UserEmailHash,
            locale: resource.Locale,
            copy: resource.Copy,
          }),
        );
      } catch {
        console.log("Move Lesson Failed");
      }
    }
  }
};
